import { useDispatch, useSelector } from 'react-redux';
import {
  errorCodeSelector,
  refreshTokenSelector,
  tokenSelector,
} from './store/selector';
import { useCallback } from 'react';
import { PostLoginRequest } from './interfaces/post-login';
import { authApiService } from './services/api';
import {
  authClearAction,
  postGoogleLoginFailureApiAction,
  postGoogleLoginInitApiAction,
  postGoogleLoginSuccessApiAction,
  postGoogleSyncFailureApiAction,
  postGoogleSyncInitApiAction,
  postGoogleSyncSuccessApiAction,
  postLoginFailureApiAction,
  postLoginInitApiAction,
  postLoginSuccessApiAction,
  postSignUpFailureApiAction,
  postSignUpInitApiAction,
  postSignUpSuccessApiAction,
} from './store/reducer';
import { courseClearAction } from '../course/store/reducer';
import { globalClearAction } from '../global/store/reducer';
import { meClearAction } from '../me/store/reducer';
import { userClearAction } from '../user/store/reducer';
import { PostGoogleLoginRequest, PostGoogleLoginResponse } from './interfaces/post-google-login';
import { PostSignUpRequest } from './interfaces/post-sign-up';
import { subscriptionClearAction } from '../subscription/store/reducer';
import { classClearAction } from '../class/store/reducer';
import { templateClearAction } from '../template/store/reducer';
import { reportClearAction } from '../report/store/reducer';
import { notificationClearAction } from '../notification/store/reducer';
import { PostGoogleSyncRequest } from './interfaces/post-google-sync';

export function useAuthCore() {
  const token = useSelector(tokenSelector);
  const refreshToken = useSelector(refreshTokenSelector);
  const errorCode = useSelector(errorCodeSelector);
  const dispatch = useDispatch();

  const login = useCallback(
    async (body: PostLoginRequest, onSuccess?: () => void, onFailure?: () => void) => {
      try {
        dispatch(postLoginInitApiAction());
        const response = await authApiService.postLogin(body);
        dispatch(postLoginSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          postLoginFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const signUp = useCallback(
    async (body: PostSignUpRequest, onSuccess?: () => void, onFailure?: () => void) => {
      try {
        dispatch(postSignUpInitApiAction());
        const response = await authApiService.postSignUp(body);
        dispatch(postSignUpSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          postSignUpFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const googleLogin = useCallback(
    async (
      body: PostGoogleLoginRequest,
      onSuccess?: (arg0: PostGoogleLoginResponse) => void,
      onFailure?: () => void
    ) => {
      try {
        dispatch(postGoogleLoginInitApiAction());
        const response = await authApiService.postGoogleLogin(body);
        dispatch(postGoogleLoginSuccessApiAction(response));
        onSuccess?.(response);
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          postGoogleLoginFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const googleSync = useCallback(
    async (body: PostGoogleSyncRequest, onSuccess?: () => void, onFailure?: () => void) => {
      try {
        dispatch(postGoogleSyncInitApiAction());
        const response = await authApiService.postGoogleSync(body);
        dispatch(postGoogleSyncSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.();
        dispatch(
          postGoogleSyncFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const clearAuth = useCallback(() => {
    dispatch(authClearAction());
  }, [dispatch]);

  const logout = useCallback(() => {
    dispatch(authClearAction());
    dispatch(classClearAction());
    dispatch(courseClearAction());
    dispatch(globalClearAction());
    dispatch(meClearAction());
    dispatch(userClearAction());
    dispatch(subscriptionClearAction());
    dispatch(templateClearAction());
    dispatch(reportClearAction());
    dispatch(notificationClearAction());
  }, [dispatch]);

  return {
    token,
    refreshToken,
    errorCode,
    login,
    signUp,
    logout,
    clearAuth,
    googleLogin,
    googleSync,
  };
}
